import { useTranslator } from '@translator-app/react';
import React from 'react';

import { ApplicationStateProvider } from '../../contexts/application-state-context';
import { GraphQLClientProvider } from '../../contexts/graphql-client';
import { useKioskMode } from '../../contexts/kiosk-context';
import {
  OrderObjectType,
  OrganisationObjectType,
  ReservationObjectType,
  ShopObjectType,
} from '../../generated/graphql';
import { PageBranding } from '../../utils/branding';
import { ErrorBoundary } from '../organisms/error-boundary';
import { ShopHeader } from '../organisms/shop-header';
import { SiteFooter } from '../organisms/site-footer';
import { PageTemplate } from './page-template';

export interface PageTemplateProps {
  children: React.ReactNode;
  organisation: OrganisationObjectType;
  shop?: ShopObjectType;
  shoppingCart: Array<ReservationObjectType>;
  reservations: Array<ReservationObjectType>;
  order?: OrderObjectType;
  titleAddition?: string;
  description?: string;
  imageUrl?: string;
  /** Woyti marketing chrome; shop pages omit header/footer. */
  branding?: PageBranding;
}

const PageImpl: React.FC<PageTemplateProps> = (props) => {
  let { children, shop, branding = 'woyti' } = props;
  const { isKioskMode } = useKioskMode();
  const showChrome = branding === 'woyti';

  return (
    <>
      {showChrome && <ShopHeader shop={shop} />}

      {children}

      {showChrome && !isKioskMode && <SiteFooter />}
    </>
  );
};

export const ShopPageTemplate: React.FC<PageTemplateProps> = (props) => {
  let {
    children,
    organisation,
    shop,
    shoppingCart,
    order,
    reservations,
    description,
    titleAddition,
    imageUrl,
    branding = 'woyti',
  } = props;
  let { translate } = useTranslator();

  const brandColor = organisation?.brandColor;
  const shopName = shop?.name || organisation?.name || 'Woyti';
  const defaultDescription = translate('seo.shopDescription', { shopName });
  const title = [shopName];
  if (titleAddition) {
    title.push(titleAddition);
  }

  return (
    <PageTemplate
      brandColor={brandColor}
      description={description || defaultDescription}
      title={title.join('|')}
      imageUrl={imageUrl}
    >
      <ApplicationStateProvider
        organisation={organisation}
        shop={shop}
        initialShoppingCart={shoppingCart}
        order={order}
        reservations={reservations}
      >
        <GraphQLClientProvider>
          <ErrorBoundary>
            <PageImpl {...props} branding={branding}>
              {children}
            </PageImpl>
          </ErrorBoundary>
        </GraphQLClientProvider>
      </ApplicationStateProvider>
    </PageTemplate>
  );
};
